// X105 (C8): "What will you use Tap Leagues for?" asked once per club. The words for each goal tile, the
// pre-tick rule (stored goals plus what the API inferred from the club's data) and the ask rule live here,
// pure, so the dialog, the layout prompt and the tests agree. The API decides which goals a club may tick,
// whether they were confirmed and (X114) whether THIS admin is asked now: "Not now" is remembered on the
// account for 7 days, so no browser, tab or session memory is kept here.
// X131 (Grant 2026-10-26): the goals are named by what the club does with them (Playing area bookings, Manage
// memberships & passes); a ticked goal that can be paid for asks "Will you charge…?" (Free / We'll charge) right under
// it, and payments is no longer a tile: it follows from those answers (any "We'll charge" ticks it, none unticks it).

/** One tile per goal: icon, bold name and ONE line saying what it means in the club's terms. */
export const GOAL_TILES = Object.freeze({
  memberships: Object.freeze({
    icon: 'mdi-card-account-details-outline',
    title: 'Manage memberships & passes',
    line: 'Memberships and passes people get and renew; competitions and bookings can ask for one.',
  }),
  bookings: Object.freeze({
    icon: 'mdi-calendar-clock-outline',
    title: 'Playing area bookings',
    line: 'Players book courts, tables or boards in the hours you set.',
  }),
  onShow: Object.freeze({
    icon: 'mdi-television-play',
    title: 'Show results on your website or a TV',
    line: 'Live standings and results in a widget on your site or on a clubhouse screen.',
  }),
  // X131: derived, never a tile of its own; kept for older frozen cards in a transcript and for the note under the list.
  payments: Object.freeze({
    icon: 'mdi-credit-card-outline',
    title: 'Take card payments',
    line: 'Card payments through Stripe, straight to the club\'s account.',
    derived: true,
  }),
});

/**
 * Grant 2026-10-25: everyone using Tap Leagues runs competitions, so the choice is not offered, but people look for it.
 * The first tile is "Run competitions": always ticked, locked (a real checked, disabled checkbox the screen reader
 * announces as such), never greyed out as if unavailable. The API ignores the key: competitions are implied.
 * X131: it carries its own follow-up, "Will you charge entry fees?".
 */
export const LOCKED_GOAL = Object.freeze({
  key: 'competitions',
  icon: 'mdi-medal-outline',
  title: 'Run competitions',
  // X121 (R55 L2): the "Always included" pill says it once; the line does not repeat it.
  line: 'Box leagues, ladders, tournaments, americano leagues and meets.',
  locked: true,
  available: true,
  selected: true,
  inferred: false,
  why: null,
});

/** X131: the goals that can be paid for, in the order they are asked; payments is ticked when any of them is charged for. */
export const CHARGEABLE = Object.freeze(['competitions', 'bookings', 'memberships']);
export const CHARGE_FREE = 'Free';
export const CHARGE_PAID = 'We\'ll charge';
/** The two choices under a ticked chargeable goal, in this order. */
export const CHARGE_CHOICES = Object.freeze([
  Object.freeze({value: false, label: CHARGE_FREE, icon: 'mdi-gift-outline'}),
  Object.freeze({value: true, label: CHARGE_PAID, icon: 'mdi-credit-card-outline'}),
]);

/** The follow-up's words (the API's ClubSetupGuide.ChargeQuestion keeps the same). */
export function chargeQuestion(key) {
  return key === 'competitions' ? 'Will you charge entry fees?' : 'Will you charge for these?';
}

/** The one line shown when anything is charged for: payments is not a choice, it follows. */
export const PAYMENTS_NOTE = 'Card payments through Stripe come first, so you can charge for these.';
/** The hint under Confirm while a ticked chargeable goal has no Free / We'll charge answer. */
export const CHARGES_HINT = 'Say Free or We\'ll charge for each ticked option.';

// ── Grant 2026-11-02: "Why not just have one other option for 'Collect payments through tap leagues' instead of the
// non obvious switch in each section?" One tickbox row after the goals replaces every Free / We'll charge follow-up,
// so nothing has to be answered before Confirm. The API still takes per-goal charges: ticked = every ticked (or
// locked) chargeable goal is charged for, unticked = none (payments follows from that on the server, as before). ──

export const COLLECT_PAYMENTS = Object.freeze({
  key: 'payments',
  icon: 'mdi-credit-card-outline',
  title: 'Collect payments through Tap Leagues',
  line: 'Entry fees, bookings or memberships paid by card, through Stripe, straight to the club\'s account.',
  locked: false,
  available: true,
});

/** True when the club can take payments at all: the API marks the goals that can be charged for only then. */
export function canCollect(tilesOrRows) {
  return (tilesOrRows ?? []).some((t) => t.chargeable);
}

/** The payments row opens ticked when anything is (or, before an answer, already looks) charged for. */
export function collectsAtStart(tilesOrRows) {
  return Object.values(preCharges(tilesOrRows)).some((v) => v === true);
}

/** The per-goal charge answers the one payments tick stands for: every chargeable goal true when ticked, false otherwise. */
export function chargesFromCollect(tilesOrRows, collect) {
  const out = {};
  for (const t of tilesOrRows ?? []) if (t.chargeable) out[t.key] = !!collect;
  return out;
}

/** Why a goal cannot be ticked yet (the feature is switched on per club by Tap Leagues). */
export function unavailableWhy(key) {
  if (key === 'bookings') return 'Ask Tap Leagues to switch bookings on first';
  if (key === 'memberships') return 'Ask Tap Leagues to switch memberships on first';
  return 'Not available for this club yet';
}

/** X131: the charge fields of an API option or row, read the same way everywhere. */
function chargeFields(o) {
  return {
    chargeable: !!o?.chargeable,
    chargeQuestion: o?.chargeQuestion ?? (o?.chargeable ? chargeQuestion(o.key) : null),
    charge: typeof o?.charge === 'boolean' ? o.charge : null,
    chargeInUse: !!o?.chargeInUse,
  };
}

/**
 * The API's goalOptions turned into tiles, in the API's order, after the locked Run competitions tile; unknown keys get
 * plain words so nothing is hidden. X131: the API's own competitions row (its charge answer) merges into the locked tile;
 * the derived payments option is never a tile.
 */
export function goalTiles(options) {
  const list = options ?? [];
  const competitions = list.find((o) => o.key === LOCKED_GOAL.key);
  const rest = list.filter((o) => o.key !== LOCKED_GOAL.key && o.key !== 'payments' && !o.derived).map((o) => ({
    key: o.key,
    available: !!o.available,
    selected: !!o.selected,
    inferred: !!o.inferred,
    // X116: the club's data says it already uses this (confirmed or not): pre-ticked and marked "Already set up".
    inUse: !!o.inUse,
    locked: false,
    icon: GOAL_TILES[o.key]?.icon ?? 'mdi-flag-outline',
    title: GOAL_TILES[o.key]?.title ?? o.title ?? o.key,
    line: GOAL_TILES[o.key]?.line ?? o.line ?? '',
    why: o.available ? null : unavailableWhy(o.key),
    ...chargeFields(o),
  }));
  return [{...LOCKED_GOAL, inUse: !!competitions?.inUse, ...chargeFields(competitions)}, ...rest];
}

/**
 * What the dialog opens with. Until an admin confirms: the stored goals, plus the goals the API read from the club's
 * data, plus (X116, Grant: "with the preexisting setup areas ticked") whatever the club already uses, so nothing it
 * uses shows as unpicked. Once CONFIRMED (X119, R54 L1, design ruling): the saved goals exactly, so an admin who
 * unticked a goal the club already uses keeps it unticked (its tile still says "Already set up"). Never an unavailable
 * goal, never the locked tile, never the derived payments option (X131).
 */
export function preTicked(options, confirmed = false) {
  return (options ?? []).filter((o) => o.key !== LOCKED_GOAL.key && o.key !== 'payments' && !o.derived && o.available && (o.selected || (!confirmed && (o.inferred || o.inUse)))).map((o) => o.key);
}

/** True when any pre-tick came from the club's data rather than a saved choice: the dialog says so in one line. Never once confirmed. */
export function anyInferred(options, confirmed = false) {
  return !confirmed && (options ?? []).some((o) => o.key !== 'payments' && !o.derived && o.available && (o.inferred || o.inUse) && !o.selected);
}

/**
 * X131: what each chargeable tile's follow-up opens with: the saved answer when there is one; otherwise "We'll charge"
 * where the club's data says it already charges (an entry fee, a priced type, a priced booking rule), and nothing at all
 * for a club with no such thing (the admin must choose). Keys: competitions, bookings, memberships; values true / false / null.
 */
export function preCharges(tiles) {
  const out = {};
  for (const t of tiles ?? []) {
    if (!t.chargeable) continue;
    out[t.key] = t.charge ?? (t.chargeInUse ? true : null);
  }
  return out;
}

/** X131: every ticked (or locked) chargeable tile has a Free / We'll charge answer, so Confirm may be pressed. */
export function chargesComplete(tiles, pickedKeys, charges) {
  const picked = new Set(pickedKeys ?? []);
  return (tiles ?? []).filter((t) => t.chargeable && (t.locked || picked.has(t.key))).every((t) => typeof charges?.[t.key] === 'boolean');
}

/** X131: the charges to save: the ticked (or locked) chargeable keys answered "We'll charge", in the asked order. */
export function chargesToSave(tiles, pickedKeys, charges) {
  const picked = new Set(pickedKeys ?? []);
  return CHARGEABLE.filter((k) => (tiles ?? []).some((t) => t.key === k && t.chargeable && (t.locked || picked.has(k))) && charges?.[k] === true);
}

/** X131: payments follows: ticked when anything ticked is charged for. */
export function paymentsDerived(tiles, pickedKeys, charges) {
  return chargesToSave(tiles, pickedKeys, charges).length > 0;
}

/** Same answers for the same keys (null and undefined alike). */
export function sameCharges(a, b, keys) {
  return (keys ?? [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])]).every((k) => (a?.[k] ?? null) === (b?.[k] ?? null));
}

/** The question itself, the API's card text and the dialog's title; names the group of tickboxes in both (X121, R55 L2). */
export const GOALS_QUESTION = 'What will you use Tap Leagues for?';
/** X119 (R54 L3): the one line under the question, the same in the dialog and the assistant's card (the API's Why). */
export const GOALS_HELP = 'Tick all that apply, then confirm. Only the parts you tick get set up; you can change this any time in the setup guide.';

/** The short marker on a row the club already uses (X116); it can still be unticked. */
export const ALREADY_SET_UP = 'Already set up';
/** The short marker on the locked Run competitions row. */
export const ALWAYS_INCLUDED = 'Always included';
/** The short marker on an unticked row, so the bits not selected read clearly (Grant 2026-10-25). */
export const NOT_PICKED = 'Not picked';
/** The one primary button under the assistant's tickbox list. */
export const CONFIRM_LABEL = 'Confirm';

/**
 * X116: the assistant's goals card is a tickbox list the API describes row by row (question.goals:
 * [{key, title, picked, locked, available, alreadySetUp}]). True when a question carries that list.
 */
export function isGoalsList(question) {
  return Array.isArray(question?.goals) && question.goals.length > 0;
}

/**
 * The API's rows turned into display rows, in the API's order: the same icon and one-line meaning as the goals
 * dialog's tiles (by key), the API's title and flags. Unknown keys get plain words so nothing is hidden.
 * X131: the API's title wins (an older frozen card keeps the words it was answered with); each row carries its
 * follow-up fields (chargeable, chargeQuestion, charge, chargeInUse).
 */
export function goalRows(goals) {
  return (goals ?? []).map((g) => {
    const tile = g.key === LOCKED_GOAL.key ? LOCKED_GOAL : GOAL_TILES[g.key];
    return {
      key: g.key,
      title: g.title || tile?.title || g.key,
      icon: tile?.icon ?? 'mdi-flag-outline',
      line: tile?.line ?? g.line ?? '',
      picked: !!g.picked || !!g.locked,
      locked: !!g.locked,
      available: g.available !== false,
      alreadySetUp: !!g.alreadySetUp,
      ...chargeFields(g),
    };
  });
}

/** The keys ticked when the list opens: the API's picks, locked rows always. */
export function rowsPicked(rows) {
  return (rows ?? []).filter((r) => r.picked || r.locked).map((r) => r.key);
}

/** X131: the follow-up answers the list opens with (see preCharges). */
export function rowsCharges(rows) {
  return preCharges(rows);
}

/** X131: the words after a title in the Confirm answer, which the API reads (SetupGoalsCards.ParseAnswer). */
export function chargeWords(key, charge) {
  if (typeof charge !== 'boolean') return null;
  if (!charge) return 'free';
  return key === 'competitions' ? 'we\'ll charge entry fees' : 'we\'ll charge';
}

/**
 * What Confirm sends: the ticked rows' titles in the list's order, the locked Run competitions first, joined as every
 * multi-select answer is ("Run competitions (we'll charge entry fees), Playing area bookings (free)"). The API reads the
 * titles and the bracketed answers; "Run competitions" alone means competitions only.
 */
export function confirmPicks(rows, pickedKeys, charges = null) {
  const keys = new Set(pickedKeys ?? []);
  return (rows ?? []).filter((r) => r.locked || keys.has(r.key)).map((r) => {
    const words = r.chargeable ? chargeWords(r.key, charges?.[r.key]) : null;
    return words ? `${r.title} (${words})` : r.title;
  });
}

/**
 * X124 (R58 M-a): what a typed short yes on the open goals card sends. When the on-screen ticks (or, X131, the Free /
 * We'll charge answers) differ from the card's starting state it is what the admin sees, as Confirm sends it; when
 * unchanged it is null, and the typed yes goes as typed (the API saves the starting ticks).
 */
export function typedYesPicks(question, pickedKeys, charges = null) {
  const rows = goalRows(question?.goals);
  if (!rows.length || !Array.isArray(pickedKeys)) return null;
  const onScreen = [...new Set([...rows.filter((r) => r.locked).map((r) => r.key), ...pickedKeys])];
  const startCharges = rowsCharges(rows);
  const chargesChanged = charges != null && !sameCharges(charges, startCharges, Object.keys(startCharges));
  if (sameGoals(onScreen, rowsPicked(rows)) && !chargesChanged) return null;
  return confirmPicks(rows, onScreen, charges ?? startCharges);
}

/** Same set, any order. */
export function sameGoals(a, b) {
  const x = [...new Set(a ?? [])].sort();
  const y = [...new Set(b ?? [])].sort();
  return x.length === y.length && x.every((k, i) => k === y[i]);
}

/** "Confirm" when nothing changed from the pre-ticks (the assistant's card says the same, X119), "Save" otherwise. X131: a changed Free / We'll charge answer counts as a change. */
export function saveLabel(picked, preticked, charges = null, precharges = null) {
  return sameGoals(picked, preticked) && (charges == null || sameCharges(charges, precharges)) ? CONFIRM_LABEL : 'Save';
}

/** The secondary button: "Not now" on the once-per-club prompt, "Cancel" on the guide's Edit. */
export function dismissLabel(prompt) {
  return prompt ? 'Not now' : 'Cancel';
}

/** X114: how long "Not now" lasts (the API's ClubSetupGuide.GoalsSnoozeDays); words only, the API keeps the date. */
export const NOT_NOW_DAYS = 7;

/** X118 (R52 L4): the setup assistant's conversation page asks the same question in its own first card, so the dialog never opens over it.
 *  R60: nor over its check and done pages, where it blocked Create setup mid-flow. */
export const GOALS_PROMPT_SKIPPED_ROUTES = ['ClubSetupAssistant', 'ClubSetupProposal', 'ClubSetupProposalDone'];

/** X118 (R52 L4): whether the goals dialog may open on this route at all. */
export function asksGoalsOnRoute(routeName) {
  return !GOALS_PROMPT_SKIPPED_ROUTES.includes(String(routeName ?? ''));
}

/**
 * Whether to open the goals dialog on this admin page: a loaded guide that says this admin is to be asked now
 * (goals never confirmed for the club, and this admin's "Not now", if any, ran out; X114, decided by the API per
 * account), for a club admin viewing as themselves (never an impersonating super admin, never the super pages),
 * and never on the setup assistant's own page (X118: its first card asks the same).
 * An older API without `askGoalsNow` falls back to the X105 rule (unconfirmed = ask).
 */
export function shouldAskGoals({guide, impersonating = false, superMode = false, routeName = null} = {}) {
  if (!guide || guide.goalsConfirmed) return false;
  if (impersonating || superMode) return false;
  if (!asksGoalsOnRoute(routeName)) return false;
  return guide.askGoalsNow ?? true;
}

// ── X187 (Grant 2026-10-27): Club settings shows the goals as plain words, with Edit opening the goals dialog ──

/** The short names the settings row uses, in the dialog's order. */
export const GOAL_SHORT_NAMES = Object.freeze({
  competitions: 'Competitions',
  bookings: 'Playing area bookings',
  memberships: 'Memberships & passes',
  onShow: 'Results on your website or a TV',
});

/**
 * "Competitions (entry fees) · Playing area bookings": what the goals dialog would open with (the saved goals once confirmed,
 * else those plus what the club already uses), each charged-for one with its "(entry fees)" / "(paid)". Competitions always first.
 */
export function goalsSummary(options, confirmed = false) {
  const tiles = goalTiles(options);
  const on = new Set(preTicked(options, confirmed));
  const charges = preCharges(tiles);
  return tiles
    .filter((t) => t.locked || on.has(t.key))
    .map((t) => {
      const name = GOAL_SHORT_NAMES[t.key] ?? t.title;
      if (!t.chargeable || charges[t.key] !== true) return name;
      return `${name} (${t.key === 'competitions' ? 'entry fees' : 'paid'})`;
    })
    .join(' · ');
}
